import classNames from 'classnames';
import type { CSSProperties, ReactElement, Ref, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { StoryCardProps, StoryCardRef } from './StoryCard.data';
import styles from './StoryCard.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './StoryCard.transitions';
import { STORY_CARD_ICON_DEFINITIONS, STORY_CARD_MASKS } from './StoryCardIcons';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import Heading from 'components/atoms/heading/Heading';
import Text from 'components/atoms/text/Text';
import Image from 'next/image';
import type { TextRef } from 'components/atoms/text/Text.data';
import type { HeadingRef } from 'components/atoms/heading/Heading.data';

export default function StoryCard({
  isVisible,
  className,
  ref,
  card,
}: StoryCardProps & { ref?: Ref<StoryCardRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement>;
  const titleRef = useRef<HeadingRef>(null);
  const descriptionRef = useRef<TextRef>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const [isInViewport, setIsInViewport] = useState(false);

  const icon = STORY_CARD_ICON_DEFINITIONS[card.icon_shape.icon];
  const iconMask = STORY_CARD_MASKS[card.icon_shape.icon];

  const imageMaskStyle: CSSProperties = {
    WebkitMaskImage: iconMask,
    WebkitMaskPosition: icon.maskPosition || 'center',
    WebkitMaskSize: icon.maskSize,
    maskImage: iconMask,
    maskPosition: icon.maskPosition || 'center',
    maskSize: icon.maskSize,
  };

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        titleRef,
        descriptionRef,
        imageRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useEffect(() => {
    const element = elementRef.current;

    if (!element) {
      setIsInViewport(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInViewport(entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0.5,
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [elementRef]);

  useChangeTransitionState(transitionController, {
    onMount: isVisible?.onMount ?? false,
    value: (isVisible?.value ?? true) && isInViewport,
  });

  return (
    <div ref={elementRef} className={classNames(styles.storyCard, className)}>
      <div className={styles.imageContainer}>
        <div className={styles.imageMask} style={imageMaskStyle}>
          <Image
            ref={imageRef}
            src={card.image.filename}
            alt={card.image.alt || ''}
            className={styles.image}
            fill
            sizes="(min-width: 768px) 50vw, 100vw"
          />
        </div>
      </div>

      <div className={styles.textContainer}>
        <Heading className={styles.title} as="h3" size="heading-03" ref={titleRef}>
          {card.title}
        </Heading>
        <Text
          ref={descriptionRef}
          as="p"
          size="body-standard"
          className={classNames(styles.description)}
        >
          {card.description}
        </Text>
      </div>
    </div>
  );
}
